'use client'

import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'

import { Button, Skeleton } from '@ui/web/components'

import { LeaderboardCard } from '../../../../components/leaderboard-card'
import { useAuthClient, useSharedTRPC } from '../../../../context'

interface LeaderboardRankingsProps {
  ids: {
    courseId?: string
    challengeId?: string
    learnEnrollmentId?: string
  }
  deferredSearch: string
}

const PAGE_SIZE = 50

function LoadingSkeletons() {
  return (
    <>
      {new Array(5).fill(0).map((_, index) => (
        <Skeleton className='srd:w-full srd:h-[88px] srd:rounded-2xl' key={`loading-leaderboard-${index}`} />
      ))}
    </>
  )
}

export function LeaderboardRankings({ ids, deferredSearch }: LeaderboardRankingsProps) {
  const trpc = useSharedTRPC()
  const authClient = useAuthClient()
  const { data: session } = authClient.useSession()
  const currentUserId = session?.user.id

  const input = useMemo(
    () => ({
      courseId: ids.courseId,
      challengeId: ids.challengeId,
      learnEnrollmentId: ids.learnEnrollmentId,
      limit: PAGE_SIZE,
      search: deferredSearch || undefined,
    }),
    [ids, deferredSearch],
  )

  const enabled = !!(ids.courseId ?? ids.challengeId ?? ids.learnEnrollmentId)

  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    trpc.leaderboard.getRankings.infiniteQueryOptions(input, {
      enabled,
      // Computed live on every read; a minute of cache keeps paging and re-renders off the database.
      staleTime: 60_000,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      initialCursor: 1,
    }),
  )

  const allEntries = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data])

  if (isError) {
    return (
      <section className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-2 srd:py-8 srd:w-full'>
        <p className='srd:text-error'>Gagal memuat data papan peringkat</p>
        <Button size='sm' onClick={() => refetch()} variant='outline' className='srd:text-neutral-white'>
          Coba lagi
        </Button>
      </section>
    )
  }

  if (isPending) {
    return (
      <section className='srd:flex srd:flex-col srd:gap-y-3 srd:w-full'>
        <LoadingSkeletons />
      </section>
    )
  }

  if (allEntries.length === 0) {
    return (
      <section className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-2 srd:py-8 srd:w-full'>
        <p className='srd:text-grey-40'>
          {deferredSearch ? 'Tidak ada peserta yang cocok' : 'Belum ada data peringkat'}
        </p>
        {!deferredSearch && (
          <Button size='sm' onClick={() => refetch()} variant='outline' className='srd:text-neutral-white'>
            Muat ulang
          </Button>
        )}
      </section>
    )
  }

  return (
    <section className='srd:flex srd:flex-col srd:gap-y-8 srd:w-full srd:items-center'>
      <section className='srd:flex srd:flex-col srd:gap-y-3 srd:w-full'>
        {allEntries.map((entry) => (
          <LeaderboardCard
            key={entry.learnEnrollmentId}
            rank={entry.rank}
            showTrophy={entry.rank >= 1 && entry.rank <= 3}
            avatarSrc={entry.photoUrl ?? ''}
            name={entry.childName}
            grade={entry.teamName ?? ''}
            schoolName={entry.organizationName ?? ''}
            xp={entry.totalXp}
            maxXp={entry.totalXpPossible}
            className={entry.userId === currentUserId ? 'srd:bg-blue-10' : undefined}
            progress={entry.progress}
          />
        ))}
        {isFetchingNextPage && <LoadingSkeletons />}
      </section>

      {hasNextPage && (
        <div className='srd:w-full srd:flex srd:items-center srd:justify-center'>
          <Button
            variant='outline'
            className='srd:rounded-full srd:px-6 srd:text-neutral-white'
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Memuat...' : 'Lihat lebih banyak'}
            <ChevronDown className='srd:w-4 srd:h-4 srd:ml-2' />
          </Button>
        </div>
      )}
    </section>
  )
}
